/** Per-day editor modal — status, note, "in-presence meeting" flag. */
import { useState, useEffect } from "preact/hooks";
import type { JSX } from "preact";
import { state, setCalendarCell, setNote, setMeeting } from "../state/store";
import { STATUS_LABEL, type StatusCode } from "../types";
import { formatDayLabel } from "../utils/dates";
import { displayName } from "../utils/names";

interface Props {
  date: string;
  who: string;
  onClose: () => void;
}

export function DayModal({ date, who, onClose }: Props): JSX.Element {
  const s = state.value;
  const initialCode = s.calendar[who]?.[date] ?? "";
  const initialNote = s.notes[who]?.[date] ?? "";
  // meetings[who][date] is now the description string (empty = flagged
  // without text). undefined = not flagged at all.
  const existingMtg = s.meetings[who]?.[date];
  const initialMeeting = existingMtg !== undefined;
  const initialMtgDesc = existingMtg ?? "";

  const [code, setCode] = useState<StatusCode | "">(initialCode);
  const [note, setNoteText] = useState(initialNote);
  const [meeting, setMtg] = useState(initialMeeting);
  const [meetingDesc, setMtgDesc] = useState(initialMtgDesc);

  // Meeting titles other people have already flagged on this day, each with
  // how many flagged it — so the user can JOIN an existing meeting (and see
  // which one the team is converging on) instead of retyping a near-duplicate.
  // Lightweight: joining just copies the title into this person's own entry —
  // no shared-meeting record, so nothing about the data model changes.
  const otherMeetingTitles = (() => {
    const map = new Map<string, number>();
    for (const p of Object.keys(s.meetings)) {
      if (p === who) continue;
      const d = s.meetings[p]?.[date];
      if (d === undefined) continue;
      const t = d.trim();
      if (!t) continue;
      map.set(t, (map.get(t) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  })();

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = (): void => {
    setCalendarCell(who, date, code === "" ? null : code);
    setNote(who, date, note.trim() || null);
    setMeeting(who, date, meeting, meetingDesc.trim());
    onClose();
  };

  const clear = (): void => {
    setCalendarCell(who, date, null);
    setNote(who, date, null);
    setMeeting(who, date, false);
    onClose();
  };

  return (
    <div class="modal-backdrop open" role="presentation" onClick={onClose}>
      <div
        class="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modalTitle"
        tabindex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="modalTitle">Edit day</h3>
        <div class="modal-date">
          {displayName(who)} — {formatDayLabel(date)}
        </div>

        <label>Status</label>
        <div class="status-grid">
          {(["P", "T", "R", "M", "V", "A", "O"] as StatusCode[]).map((c) => (
            <button key={c}
              class={code === c ? "active" : ""}
              onClick={() => setCode(code === c ? "" : c)}
              aria-pressed={code === c}
            >
              <div class={`swatch s-${c}`} />
              {STATUS_LABEL[c]}
            </button>
          ))}
        </div>

        <label for="modalNote" style="margin-top:14px">
          Note (optional)
        </label>
        <textarea
          id="modalNote"
          value={note}
          onInput={(e) => setNoteText((e.target as HTMLTextAreaElement).value)}
          placeholder="e.g. Working on Acme proposal, available 9–13"
        />

        <label class="checkbox-row" style="margin-top:8px">
          <input
            type="checkbox"
            checked={meeting}
            onChange={(e) => setMtg((e.target as HTMLInputElement).checked)}
          />
          In-presence meeting on this day (visible to the team)
        </label>
        {meeting && (
          <div class="meeting-desc-field">
            {otherMeetingTitles.length > 0 && (
              <div class="meeting-join">
                <span class="meeting-join-label">
                  Already flagged today — tap to join one, or type a new one below:
                </span>
                <div class="meeting-chip-row">
                  {otherMeetingTitles.map(([t, n]) => (
                    <button
                      type="button"
                      key={t}
                      class={`meeting-chip${meetingDesc.trim() === t ? " selected" : ""}`}
                      onClick={() => setMtgDesc(t)}
                      title={`${t} — ${n} ${n === 1 ? "person" : "people"} so far`}
                    >
                      <span class="meeting-chip-label">{t}</span>
                      <span class="meeting-chip-count" aria-label={`${n} ${n === 1 ? "person" : "people"}`}>{n}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <label for="modalMeetingDesc">Meeting description (optional)</label>
            <input
              id="modalMeetingDesc"
              type="text"
              value={meetingDesc}
              onInput={(e) => setMtgDesc((e.target as HTMLInputElement).value)}
              placeholder="e.g. Q3 review with Acme · 10:00–12:00"
              maxLength={120}
            />
            <div class="hint">
              This shows in the pinned <em>In-presence meetings</em> row of the
              Company Overview so colleagues know what's happening.
            </div>
          </div>
        )}

        <div class="modal-actions">
          <button type="button" class="modal-secondary clear-btn" onClick={clear}>
            Clear day
          </button>
          <button type="button" class="modal-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" class="modal-primary save-btn" onClick={save}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
